import React, { useContext } from 'react'
import { string, bool, func } from 'prop-types'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import ItemDetailsViewContainer from 'containers/designSidepanel/ItemDetailsViewContainer'
import * as modes from 'models/ui/constants'
import Products from 'components/designSidepanel/Products'
import { productsDataType, productDataType } from 'models/propTypes'
import { FURNITURE_SWAP_NUM_COLUMNS } from 'models/ui/constants'
import Icon from 'components/uiElements/Icon'
import ProductSummary from 'components/designSidepanel/ProductSummary'
import Tooltip from 'components/Tooltip'
import SugarcubeContext from 'apps/SugarcubeContext'
import ImagePicker from 'components/designSidepanel/ImagePicker'
import Button from '@ingka/button'
import ProductItemContainer from 'containers/designSidepanel/ProductItemContainer'
import Accordion, { AccordionItem } from '@ingka/accordion'
import * as RenderItemTypes from 'constants/renderItemTypes'
import OptionSelectorViewContainer from 'containers/designSidepanel/OptionSelectorViewContainer'
import FavoriteButtonContainer from 'containers/uiElements/FavoriteButtonContainer'
import { TRACK_LOCATION_SIDEPANEL } from 'models/tracking/constants'
import AdminProductDetailsContainer from 'containers/admin/AdminProductDetailsContainer'

require('./SwapView.scss')

const SwapView = ({
    swappableItemsData,
    isLoading,
    mode,
    onToggle,
    selectedSwapFurnitureData,
    trackShowDetails,
    selectedFurnitureData,
    onUndoSwap,
    addToBag,
}) => {
    const { t } = useTranslation()

    const isDetailsVisible = mode === modes.DESIGN_SIDEPANEL_MODE_SWAP_DETAILS
    const { isMobile, isKiosk } = useContext(SugarcubeContext)

    const hasNoSelectedFurniture = !selectedFurnitureData
    const selectedOriginalSwapFurniture =
        selectedFurnitureData && selectedSwapFurnitureData && selectedFurnitureData.id === selectedSwapFurnitureData.id

    const undoSwapCallback = selectedOriginalSwapFurniture ? null : onUndoSwap
    const undoSwapTooltipText = !selectedOriginalSwapFurniture ? t('tooltip.revert to original') : null

    const hasSwappableItems = !!(swappableItemsData && swappableItemsData.length) && !isLoading
    const isArtItem = (selectedFurnitureData && selectedFurnitureData.renderType === RenderItemTypes.ART_ITEM) || false

    if (isMobile) {
        return (
            <div className="SwapView">
                {isDetailsVisible && (
                    <div className={classNames('detailsWrapper', { isDetailsVisible })}>
                        <ItemDetailsViewContainer />
                    </div>
                )}
                <div
                    className={classNames('browseWrapper', {
                        isBrowseVisible: !isDetailsVisible,
                        mobile: true,
                    })}
                >
                    <div className="swapHeader">
                        <div className="headerTitle">{t('design.swap products')}</div>
                        <div className={classNames('closeMobile')} onClick={onToggle}>
                            <Icon code="cross" />
                        </div>
                    </div>
                    <div className={classNames('itemSelection', { mobile: true })}>
                        <div className="items">
                            <Products
                                furnituresData={swappableItemsData}
                                furnitureIsLoading={isLoading}
                                columnCount={FURNITURE_SWAP_NUM_COLUMNS}
                                listLayout="horizontal"
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="SwapView">
            {hasNoSelectedFurniture ? (
                <div className="noneSelectedContainer">
                    <div className="noneSelectedImage">
                        <img src={`${ASSET_BUCKET}/graphics/chairAndPin.png`} alt={t('design.alt.product with pin')} />
                    </div>
                    <div className="noneSelectedText">
                        <div className="noneSelectedHeader">{t('design.select a product')}</div>
                        <div>{t('design.select or add a product')}</div>
                    </div>
                </div>
            ) : (
                <>
                    {isDetailsVisible && (
                        <div className={classNames('detailsWrapper', { isDetailsVisible })}>
                            <ItemDetailsViewContainer />
                        </div>
                    )}
                    <div
                        className={classNames('browseWrapper', {
                            isBrowseVisible: !isDetailsVisible,
                            mobile: false,
                        })}
                    >
                        <div className="itemSummary">
                            <div className="thumbContainer">
                                <ImagePicker
                                    images={selectedFurnitureData.imageUrls}
                                    thumbUrl={selectedFurnitureData.thumbUrl}
                                    name={selectedFurnitureData.name}
                                />
                            </div>
                            <div>
                                <ProductSummary
                                    productData={selectedFurnitureData}
                                    trackShowDetails={trackShowDetails}
                                />
                                <AdminProductDetailsContainer productData={selectedFurnitureData} />
                                <div className="ctaContainer">
                                    {!isKiosk && (
                                        <Button
                                            className="cartCta"
                                            type="emphasised"
                                            onClick={addToBag}
                                            data-testid="item-details-add-to-bag-btn"
                                            text={t('design.add to bag')}
                                        />
                                    )}
                                    {selectedFurnitureData && (
                                        <FavoriteButtonContainer
                                            localItemNumber={selectedFurnitureData.localItemNumber}
                                            location={TRACK_LOCATION_SIDEPANEL}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        {isArtItem && (
                            <Accordion className="customizeAccordion">
                                <AccordionItem
                                    title={
                                        <h3 className="customizeTitle">
                                            <Icon code="color_wheel" svg />
                                            <span>{t('design.customize')}</span>
                                        </h3>
                                    }
                                >
                                    <OptionSelectorViewContainer />
                                </AccordionItem>
                            </Accordion>
                        )}

                        {hasSwappableItems && (
                            <>
                                <h3 className="swapViewTitle">
                                    <div className="swapViewHeaderText">{t('design.swap product')}</div>
                                    {selectedSwapFurnitureData.thumbUrl && !selectedOriginalSwapFurniture && (
                                        <>
                                            {undoSwapTooltipText && (
                                                <Tooltip id="swap-view-revert-swap" place="bottom" />
                                            )}
                                            <button
                                                className="undoButtonContainer"
                                                onClick={undoSwapCallback}
                                                data-for="swap-view-revert-swap"
                                                data-tip={undoSwapTooltipText}
                                            >
                                                <Icon code="arrowCounterclockwise" />

                                                <img
                                                    className="productThumbImage"
                                                    src={selectedSwapFurnitureData.thumbUrl}
                                                    alt=""
                                                />
                                            </button>
                                        </>
                                    )}
                                </h3>
                                <div className={classNames('itemSelection', { mobile: false })}>
                                    <div className="items">
                                        {swappableItemsData &&
                                            swappableItemsData.map((productData) => (
                                                <ProductItemContainer
                                                    furnitureData={productData}
                                                    key={productData.uniqueId || productData.id}
                                                />
                                            ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

SwapView.defaultProps = {
    swappableItemsData: null,
    onToggle: null,
    selectedSwapFurnitureData: null,
    selectedFurnitureData: null,
}

SwapView.propTypes = {
    swappableItemsData: productsDataType,
    isLoading: bool.isRequired,
    mode: string.isRequired,
    onToggle: func,
    selectedSwapFurnitureData: productDataType,
    trackShowDetails: func.isRequired,
    selectedFurnitureData: productDataType,
    onUndoSwap: func.isRequired,
    addToBag: func.isRequired,
}

export default SwapView
